import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Package, Warehouse, CheckCircle2, Trash2, Users, ArrowRight, Plus, Server, HardDrive, Cpu, Database } from "lucide-react";

const StatCard = React.memo(({ label, value, accent, icon: Icon, testid, onClick }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-left border-2 border-black p-5 brutal-shadow brutal-shadow-hover bg-white group`}
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-11 h-11 flex items-center justify-center border-2 border-black ${accent}`}>
        <Icon className="w-6 h-6" strokeWidth={2.5} />
      </div>
      <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={3} />
    </div>
    <div className="font-display text-5xl font-black leading-none tracking-tighter">{value ?? "—"}</div>
    <div className="text-[10px] font-black uppercase tracking-[0.25em] mt-3">{label}</div>
  </button>
));

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [serverStatus, setServerStatus] = useState(null);
  const [serverLoading, setServerLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/status")
      .then((r) => setServerStatus(r.data))
      .catch(() => {})
      .finally(() => setServerLoading(false));
  }, []);

  const formatBytes = (bytes) => {
    if (bytes == null) return "—";
    const gb = bytes / 1024 / 1024 / 1024;
    return `${gb.toFixed(1)} GB`;
  };

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/depositi?search=${encodeURIComponent(query.trim())}`);
    } else {
      navigate("/depositi");
    }
  };

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* Hero */}
      <section className="border-2 border-black bg-white p-6 md:p-10 relative overflow-hidden tread-bg">
        <div className="absolute top-0 right-0 diag-stripes w-32 h-4" />
        <div className="relative">
          <div className="text-[11px] font-black uppercase tracking-[0.3em] text-[#E60000] mb-3">
            Centro di Controllo
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.9]">
            MAGAZZINO<br />
            <span className="text-[#E60000]">CLIENTI</span>
          </h1>
          <p className="mt-4 text-base font-medium max-w-2xl">
            Gestione clienti e pneumatici in tempo reale: trova ogni set di gomme
            in pochi secondi e tieni sotto controllo i magazzini di <strong>Vasto</strong> e <strong>Cupello</strong>.
          </p>

          {/* Quick search */}
          <form onSubmit={onSearch} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-3xl">
            <div className="flex-1 flex items-center border-2 border-black bg-white">
              <Search className="w-5 h-5 ml-3" strokeWidth={3} />
              <input
                data-testid="quick-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca cliente, telefono, targa, marca o misura..."
                className="flex-1 p-3 outline-none font-medium bg-transparent"
              />
            </div>
            <button
              type="submit"
              data-testid="quick-search-submit"
              className="bg-[#E60000] text-white border-2 border-black px-6 py-3 font-bold uppercase tracking-widest hover:bg-black transition-colors"
            >
              Cerca
            </button>
            <button
              type="button"
              data-testid="new-deposit-shortcut"
              onClick={() => navigate("/depositi/nuovo")}
              className="bg-black text-white border-2 border-black px-6 py-3 font-bold uppercase tracking-widest hover:bg-[#E60000] transition-colors inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" strokeWidth={3} />
              Nuovo
            </button>
          </form>
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">
          Panoramica
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
          <StatCard
            testid="stat-attivi"
            label="Depositi Attivi"
            value={stats?.totale_attivi}
            icon={Package}
            accent="bg-[#E60000] text-white"
            onClick={() => navigate("/depositi?stato=Attivo")}
          />
          <StatCard
            testid="stat-vasto"
            label="Mag. Vasto"
            value={stats?.vasto}
            icon={Warehouse}
            accent="bg-black text-white"
            onClick={() => navigate("/depositi?stato=Attivo&magazzino=Vasto")}
          />
          <StatCard
            testid="stat-cupello"
            label="Mag. Cupello"
            value={stats?.cupello}
            icon={Warehouse}
            accent="bg-black text-white"
            onClick={() => navigate("/depositi?stato=Attivo&magazzino=Cupello")}
          />
          <StatCard
            testid="stat-ritirati"
            label="Ritirati"
            value={stats?.ritirati}
            icon={CheckCircle2}
            accent="bg-white text-black"
            onClick={() => navigate("/depositi?stato=Ritirato")}
          />
          <StatCard
            testid="stat-smaltiti"
            label="Smaltiti"
            value={stats?.smaltiti}
            icon={Trash2}
            accent="bg-[#E60000] text-white"
            onClick={() => navigate("/depositi?stato=Smaltito")}
          />
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">
          Capienza Magazzini
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <CapacityCard
            testid="capacity-vasto"
            name="Vasto"
            occupati={stats?.vasto_posti_occupati}
            totali={stats?.vasto_posti_totali}
            liberi={stats?.vasto_posti_liberi}
            pct={stats?.vasto_riempimento_pct}
          />
          <CapacityCard
            testid="capacity-cupello"
            name="Cupello"
            occupati={stats?.cupello_posti_occupati}
            totali={stats?.cupello_posti_totali}
            liberi={stats?.cupello_posti_liberi}
            pct={stats?.cupello_riempimento_pct}
          />
        </div>
      </section>

      {/* Clients summary */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          data-testid="card-clienti"
          onClick={() => navigate("/clienti")}
          className="text-left border-2 border-black p-6 brutal-shadow brutal-shadow-hover bg-black text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#E60000]">Anagrafica</div>
              <div className="font-display font-black text-3xl mt-2 uppercase">
                {stats?.totale_clienti ?? "—"} Clienti
              </div>
              <div className="text-sm mt-1 opacity-80">Gestisci anagrafica, contatti e indirizzi</div>
            </div>
            <Users className="w-12 h-12" strokeWidth={2.5} />
          </div>
        </button>
        <button
          data-testid="card-import"
          onClick={() => navigate("/import")}
          className="text-left border-2 border-black p-6 brutal-shadow brutal-shadow-hover bg-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#E60000]">Strumenti</div>
              <div className="font-display font-black text-3xl mt-2 uppercase">
                Importa Inventario
              </div>
              <div className="text-sm mt-1">Carica file CSV / Excel esistente</div>
            </div>
            <Package className="w-12 h-12" strokeWidth={2.5} />
          </div>
        </button>
      </section>

      {/* Stato Server — Ultima sezione */}
      <section>
        <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">
          Stato Server
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border-2 border-black bg-white p-6 brutal-shadow">
            <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#E60000] mb-3">
              Stato servizi
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatusItem label="Server" value={serverStatus?.backend_online ? "Online" : "Offline"} active={serverStatus?.backend_online} loading={serverLoading} />
              <StatusItem label="MongoDB" value={serverStatus?.mongo_online ? "Online" : "Offline"} active={serverStatus?.mongo_online} loading={serverLoading} />
              <StatusItem label="Ultimo backup" value={serverStatus?.last_backup?.name || "Nessun backup"} loading={serverLoading} />
              <StatusItem label="Versione" value={serverStatus?.version || "—"} loading={serverLoading} />
            </div>
          </div>

          <div className="border-2 border-black bg-white p-6 brutal-shadow">
            <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#E60000] mb-3">
              Risorse
            </div>
            <div className="grid grid-cols-1 gap-3">
              <StatusItem label="CPU" value={serverStatus ? `${serverStatus.cpu_percent}%` : "—"} loading={serverLoading} />
              <StatusItem label="RAM" value={serverStatus ? `${serverStatus.ram_percent}%` : "—"} loading={serverLoading} />
              <StatusItem label="Disco libero" value={serverStatus ? formatBytes(serverStatus.disk_free_bytes) : "—"} loading={serverLoading} />
              <StatusItem label="Disco usato" value={serverStatus ? `${serverStatus.disk_used_percent}%` : "—"} loading={serverLoading} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const StatusItem = React.memo(({ label, value, active, loading }) => (
  <div className="border-2 border-black bg-white p-4">
    <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#E60000] mb-2">{label}</div>
    <div className={`font-display font-black text-lg ${active === false ? "text-red-600" : "text-black"}`}>
      {loading ? "Caricamento..." : value}
    </div>
  </div>
));

const CapacityCard = React.memo(({ testid, name, occupati, totali, liberi, pct }) => {
  const fillPct = Math.min(100, Math.max(0, pct ?? 0));
  return (
    <div
      data-testid={testid}
      className="border-2 border-black bg-white p-5 brutal-shadow"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-display font-black text-2xl uppercase">Magazzino {name}</div>
        <div className="font-mono-data font-black text-3xl">{fillPct.toFixed(1)}%</div>
      </div>
      <div className="h-4 border-2 border-black bg-white relative overflow-hidden mb-3">
        <div
          className="h-full bg-[#E60000] transition-all"
          style={{ width: `${fillPct}%` }}
        />
        {fillPct > 80 && (
          <div className="absolute inset-0 diag-stripes opacity-20" />
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Mini label="Occupati" value={occupati} accent="text-black" />
        <Mini label="Liberi" value={liberi} accent="text-[#E60000]" />
        <Mini label="Totali" value={totali} accent="text-black/60" />
      </div>
    </div>
  );
});

const Mini = React.memo(({ label, value, accent }) => (
  <div className="border border-black/20 p-2">
    <div className={`font-display font-black text-xl ${accent}`}>{value ?? "—"}</div>
    <div className="text-[9px] font-black uppercase tracking-widest mt-0.5">{label}</div>
  </div>
));
